/**
 * Verbose diagnostics for Ask Agent → suggested edit → track-changes XML.
 * Grep terminal logs for [SuggestEdit].
 */

import { log, logWarn, logError } from './logging';

const PREFIX = '[SuggestEdit]';

export interface TextSnapshot {
  len: number;
  trimmedLen: number;
  leadingWhitespaceChars: number;
  trailingWhitespaceChars: number;
  doubleSpaceCount: number;
  preview: string;
  tail: string;
  normalized: string;
  normalizedLen: number;
}

export function textSnapshot(text: string, maxPreview = 200): TextSnapshot {
  const preview = text.slice(0, maxPreview);
  const tail = text.slice(Math.max(0, text.length - 80));
  const normalized = text.replace(/\s+/g, ' ').trim();
  return {
    len: text.length,
    trimmedLen: text.trim().length,
    leadingWhitespaceChars: text.length - text.trimStart().length,
    trailingWhitespaceChars: text.length - text.trimEnd().length,
    doubleSpaceCount: (text.match(/  +/g) || []).length,
    preview,
    tail,
    normalized,
    normalizedLen: normalized.length,
  };
}

export function logSuggestEdit(stage: string, detail: Record<string, unknown>): void {
  log(`${PREFIX} ${stage}`, detail);
}

export function logSuggestEditWarn(stage: string, detail: Record<string, unknown>): void {
  logWarn(`${PREFIX} ${stage}`, detail);
}

export function logSuggestEditError(stage: string, detail: Record<string, unknown>): void {
  logError(`${PREFIX} ${stage}`, detail);
}

/** Compare two strings likely used as diff baselines (API plain text vs OOXML accepted text). */
export function logTextBaselineComparison(
  labelA: string,
  textA: string,
  labelB: string,
  textB: string,
): void {
  const snapA = textSnapshot(textA);
  const snapB = textSnapshot(textB);
  const normMatch = snapA.normalized === snapB.normalized;
  logSuggestEdit('TEXT_BASELINE_COMPARE', {
    normMatch,
    [labelA]: snapA,
    [labelB]: snapB,
    normalizedDiffPreview: normMatch
      ? null
      : {
          aOnlyPrefix: snapA.normalized.slice(0, 80),
          bOnlyPrefix: snapB.normalized.slice(0, 80),
        },
  });
  if (!normMatch) {
    logSuggestEditWarn('TEXT_BASELINE_MISMATCH', {
      hint:
        'Diff uses OOXML accepted text; API may use trimmed plain text — misalignment causes wrong deletions.',
      [labelA + 'WordCount']: snapA.normalized.split(/\s+/).filter(Boolean).length,
      [labelB + 'WordCount']: snapB.normalized.split(/\s+/).filter(Boolean).length,
    });
  }
}

export function logWordList(label: string, words: string[], max = 40): void {
  logSuggestEdit('WORD_LIST', {
    label,
    count: words.length,
    words: words.slice(0, max),
    truncated: words.length > max,
  });
}

export interface DiffOpLogEntry {
  index: number;
  type: string;
  textPreview: string;
  startPos: number;
  endPos: number;
  originalSlice?: string;
}

export function logDiffOperations(
  originalText: string,
  ops: Array<{ type: string; text: string; startPos: number; endPos: number }>,
): void {
  const entries: DiffOpLogEntry[] = ops.map((op, index) => ({
    index,
    type: op.type,
    textPreview: op.text.length > 120 ? op.text.slice(0, 120) + '…' : op.text,
    startPos: op.startPos,
    endPos: op.endPos,
    originalSlice:
      op.type !== 'insertion'
        ? originalText.slice(op.startPos, Math.min(op.endPos, op.startPos + 80))
        : `(insert @${op.startPos})`,
  }));
  logSuggestEdit('DIFF_OPS_DETAIL', { count: ops.length, ops: entries });
}
